import {clamp} from 'lodash';
import type {PlacementIdentifier} from '@packages/utilities/box';

export const arc = (x: number, y: number): string =>
  x && y ? ['a', x, y, 0, 0, 1, x, y].join(' ') : '';

export const move = (x: number, y: number): string => 'M' + x + ' ' + y;
export const horizontal = (x: number): string => 'h' + x;
export const vertical = (y: number): string => 'v' + y;
export const line = (...args: Array<any>): string => 'l' + args.join(' ');

type Balloon = {
  position: PlacementIdentifier;
  left?: number;
  top?: number;
  width: number;
  height: number;
  borderRadius?: number;
  arrowSize?: number;
  arrowOffset?: number;
};

export const balloon = ({
  position = 'top',
  left = 0,
  top = 0,
  width,
  height,
  borderRadius = 0,
  arrowSize = 0,
  arrowOffset = 0,
}: Balloon): string => {
  const doubleBorderRadius = 2 * borderRadius;
  const arrowSizePlusBorderRadius = arrowSize + borderRadius;
  const clampedArrowOffset = arrowSize
    ? clamp(
        arrowOffset,
        arrowSizePlusBorderRadius,
        (position === 'top' || position === 'bottom' ? width : height) -
          arrowSizePlusBorderRadius
      )
    : 0;

  return [
    move(left, top + borderRadius),
    arc(borderRadius, -borderRadius),
    arrowSize && position === 'bottom'
      ? line(
          clampedArrowOffset - arrowSizePlusBorderRadius,
          0,
          arrowSize,
          -arrowSize,
          arrowSize,
          arrowSize,
          width - clampedArrowOffset - arrowSizePlusBorderRadius,
          0
        )
      : horizontal(width - doubleBorderRadius),
    arc(borderRadius, borderRadius),
    arrowSize && position === 'left'
      ? line(
          0,
          clampedArrowOffset - arrowSizePlusBorderRadius,
          arrowSize,
          arrowSize,
          -arrowSize,
          arrowSize,
          0,
          height - clampedArrowOffset - arrowSizePlusBorderRadius
        )
      : vertical(height - doubleBorderRadius),
    arc(-borderRadius, borderRadius),
    arrowSize && position === 'top'
      ? line(
          -width + clampedArrowOffset + arrowSizePlusBorderRadius,
          0,
          -arrowSize,
          arrowSize,
          -arrowSize,
          -arrowSize,
          -clampedArrowOffset + arrowSizePlusBorderRadius,
          0
        )
      : horizontal(-width + doubleBorderRadius),
    arc(-borderRadius, -borderRadius),
    arrowSize && position === 'right'
      ? line(
          0,
          -height + clampedArrowOffset + arrowSizePlusBorderRadius,
          -arrowSize,
          -arrowSize,
          arrowSize,
          -arrowSize,
          'z'
        )
      : 'z',
  ].join('');
};

// this should be a balloon, but the Box type is not compatible
export const cutout = (...boxes: Array<Record<any, any>>): string =>
  // @ts-expect-error - TS2345 - Argument of type '({ position, left, top, width, height, borderRadius, arrowSize, arrowOffset, }: Balloon) => string' is not assignable to parameter of type '(value: Record<any, any>, index: number, array: Record<any, any>[]) => string'.
  boxes.map(balloon).join('');

const relativeRect = (x: number, y: number, width: number, height: number) =>
  `m${x} ${y}h${width}v${height}h${-width}z`;

const relativeMove = (x: number, y: number) => `m${x} ${y}`;

export const grid = ({
  top = 0,
  left = 0,
  width,
  height,
  columns,
  rows,
  gutter,
}: {
  left?: number;
  top?: number;
  width: number;
  height: number;
  columns: number;
  rows: number;
  gutter: number;
}): string => {
  const cellWidthWithGutter = (width - gutter) / columns;
  const cellWidth = cellWidthWithGutter - gutter;
  const cellHeightWithGutter = (height - gutter) / rows;
  const cellHeight = cellHeightWithGutter - gutter;

  return (
    relativeRect(left, top, width, height) +
    relativeMove(width - cellWidthWithGutter, -cellHeight) +
    (
      relativeMove(-width + gutter, cellHeightWithGutter) +
      relativeRect(cellWidthWithGutter, 0, cellWidth, cellHeight).repeat(
        columns
      )
    ).repeat(rows)
  );
};
